import { escapeHtml } from '../ui.js';
import {
  dayKey, isOpen, uiState, byInstruction, boardColumns, projectBrief, hm, dueText, waitingParty, waitAge,
  isLate, daysLate,
} from '../tasks-v2.js';

// PROJECT RECORD › TASKS (design_handoff_tasks_v2, "Project record › Tasks").
//
// The tab answers "what is outstanding on this project, on which instruction,
// and who has it". By instruction (default): one group per live instruction
// with its time against budget — the instruction is the billable unit, so time
// on a task with no instruction "cannot be billed" and the group says so. Board:
// the four states as columns, no drag (the panel moves state).
//
// Pure HTML from a view-model (projectTasksHtml) so the tests can read it; the
// page (project-detail.js) owns every write and passes handlers in.
//
//   vm.tasks         this project's tasks
//   vm.instructions  the project's instructions ({ seq, title|description, status, budgetHours })
//   vm.minsBySeq     instruction seq → minutes logged (timesheets)
//   vm.timePending   the timesheets have not arrived yet — say so, never 0:00
//   vm.people        teamFrom(...) — { userId, name }
//   vm.names         userId → display name, for owners who are not on the team any more
//   vm.projectNumber "8570"
//   vm.mode          'list' | 'board'
//   vm.showDone      list only
//   vm.addInstr      the instruction picked on the add line (seq string, '' = none, undefined = newest)
//   vm.updatedAt     'HH:MM' the tasks were read
//   vm.note          a write that failed, said inline
//   vm.today         'YYYY-MM-DD'

const first = (name) => String(name || '').trim().split(/\s+/)[0] || '';
const pad2 = (n) => String(n).padStart(2, '0');
const dm = (iso) => (iso ? `${pad2(Number(String(iso).slice(8, 10)))}/${pad2(Number(String(iso).slice(5, 7)))}` : '');
const dayOf = (v) => (v ? String(v).slice(0, 10) : '');

/** The instruction's short name: its title, else the first line of its description. */
export function instrTitle(i) {
  const raw = String((i && (i.title || i.description)) || 'Original scope').split('\n')[0].trim();
  return raw.length > 70 ? `${raw.slice(0, 68).trimEnd()}…` : raw;
}
/** "In Progress" → "In progress"; V1 statuses arrive title-cased. */
const sentence = (s) => { const t = String(s || '').trim(); return t ? t[0].toUpperCase() + t.slice(1).toLowerCase() : ''; };
const LIVE_DOT = /^(in progress|review|ready to issue|part issued|issued)$/i;

/** The row's source chip (EMAIL, SYSTEM, ASK TRACK, SITE FLAG…). Copied small
 *  rather than imported from task-row.js, which the Tasks page owns. */
export function sourceChip(t) {
  if (!t || t.state === 'done') return '';
  const siteFlag = (t.source && typeof t.source === 'object' && t.source.kind === 'site-flag')
    || t.sourceLabel === 'SITE FLAG' || (typeof t.sourceRef === 'string' && /\/siteChecks\/[^#]*#/.test(t.sourceRef));
  let label = '';
  if (siteFlag) label = 'SITE FLAG';
  else if (t.sourceLabel) label = String(t.sourceLabel);
  else if (t.source === 'agent') label = 'ASK TRACK';
  else if (t.source === 'system') label = 'SYSTEM';
  return label ? `<span class="pt2-src">${escapeHtml(label.toUpperCase())}</span>` : '';
}

function nameFn(vm) {
  const people = vm.people || [];
  const names = vm.names || {};
  return (id) => {
    if (!id) return '';
    const p = people.find((x) => x.userId === id);
    return first((p && p.name) || names[id] || id);
  };
}

/** What the STATE cell says, and its tone (late/wait = orange, check = blue). */
export function stateOf(t, vm, today) {
  const name = nameFn(vm);
  const s = uiState(t);
  if (s === 'done') return { text: 'Done', tone: 'done' };
  if (s === 'waiting') return { text: `Waiting on ${waitingParty(t, vm.people || []).who}`, tone: 'hot' };
  if (s === 'checking') return { text: t.checkerId ? `With ${name(t.checkerId)} to check` : 'With checker', tone: 'check' };
  if (isLate(t, today)) return { text: 'Overdue', tone: 'hot' };
  return { text: 'Open', tone: 'open' };
}

/** What the DUE cell says. */
export function dueOf(t, today) {
  const s = uiState(t);
  if (s === 'done') return { text: 'Done', tone: 'muted' };
  if (s === 'waiting') return { text: `${waitAge(t, today)}d waiting`, tone: 'hot' };
  if (isLate(t, today)) return { text: dueText(t, today), tone: 'hot' };
  return t.dueDate ? { text: dueText(t, today), tone: '' } : { text: '—', tone: 'muted' };
}

function rowHtml(t, vm, today) {
  const name = nameFn(vm);
  const done = t.state === 'done';
  const st = stateOf(t, vm, today);
  const due = dueOf(t, today);
  const who = t.assigneeId ? name(t.assigneeId) : '';
  return `<div class="pt2-row" data-open="${escapeHtml(t.id)}" role="button" tabindex="0"${done ? ' data-done' : ''}>
    <button type="button" class="pt2-tick" data-tick="${escapeHtml(t.id)}" role="checkbox" aria-checked="${done}"
            aria-label="${done ? 'Mark not done' : 'Mark done'}">${done ? '&#10003;' : ''}</button>
    <span class="pt2-task"><span class="pt2-title">${escapeHtml(t.title || '')}</span>${sourceChip(t)}</span>
    <span class="pt2-who"${who ? '' : ' data-none'}>${escapeHtml(who || 'Unassigned')}</span>
    <span class="pt2-state" data-tone="${st.tone}"><span class="pt2-dot" aria-hidden="true"></span>${escapeHtml(st.text)}</span>
    <span class="pt2-due" data-tone="${due.tone}">${escapeHtml(due.text)}</span>
  </div>`;
}

function groupHtml(g, vm, today) {
  const rows = g.tasks.map((t) => rowHtml(t, vm, today)).join('');
  if (g.none) {
    return `<div class="pt2-group" data-none>
      <div class="pt2-gh">
        <span class="pt2-gl"><span class="pt2-ref" data-none>&mdash;</span><span class="pt2-gt">Not on an instruction</span></span>
        <span class="pt2-gm"><span>${g.open} open &middot; time here cannot be billed</span></span>
      </div>${rows}</div>`;
  }
  const i = g.instruction || {};
  const status = sentence(i.status);
  const time = vm.timePending
    ? '<span class="pt2-time" title="The timesheets are still arriving">&hellip;</span>'
    : `<span class="pt2-time"${g.over ? ' data-over' : ''}>${escapeHtml(hm(g.usedMins))}${g.budgetMins != null ? ` of ${escapeHtml(hm(g.budgetMins))}` : ''}</span>`;
  return `<div class="pt2-group" data-seq="${escapeHtml(g.seq)}"${g.closed ? ' data-closed' : ''}>
    <div class="pt2-gh">
      <span class="pt2-gl"><span class="pt2-ref">I${escapeHtml(g.seq)}</span><span class="pt2-gt">${escapeHtml(instrTitle(i))}</span>${status
        ? `<span class="pt2-gs"${LIVE_DOT.test(status) && !g.closed ? ' data-live' : ''}><span class="pt2-dot" aria-hidden="true"></span>${escapeHtml(status)}</span>` : ''}</span>
      <span class="pt2-gm"><span>${g.open ? `${g.open} open` : 'Nothing open'}</span><span>Time ${time}</span></span>
    </div>${rows}</div>`;
}

function cardHtml(t, vm, today) {
  const name = nameFn(vm);
  const s = uiState(t);
  const done = s === 'done';
  let fig = '', hot = false;
  if (s === 'waiting') { fig = `${waitAge(t, today)}d`; hot = true; }
  else if (s === 'checking') fig = t.checkerId ? name(t.checkerId) : '';
  else if (done) fig = dm(dayOf(t.completedAtDay || t.completedAt));
  else if (isLate(t, today)) { fig = `${daysLate(t, today)}d late`; hot = true; }
  else fig = dueText(t, today);
  let wait = '';
  if (s === 'waiting') {
    const p = waitingParty(t, vm.people || []);
    wait = `<span class="pt2-cwait">${escapeHtml([p.who, p.detail].filter(Boolean).join(' · '))}</span>`;
  }
  const ref = t.instructionId ? `I${t.instructionId}` : '—';
  return `<div class="pt2-card" data-open="${escapeHtml(t.id)}" role="button" tabindex="0"${done ? ' data-done' : ''}>
    <span class="pt2-ct">${escapeHtml(t.title || '')}</span>${wait}
    <span class="pt2-cm"><span class="pt2-cref">${escapeHtml(ref)}</span><span>${escapeHtml(t.assigneeId ? name(t.assigneeId) : 'Unassigned')}</span>
      <span class="pt2-cfig"${hot ? ' data-hot' : ''}>${escapeHtml(fig)}</span></span>
  </div>`;
}

function boardHtml(tasks, vm, today) {
  return `<div class="pt2-board">${boardColumns(tasks, today).map((c) => `
    <div class="pt2-col" data-col="${c.key}">
      <div class="pt2-colh"><span class="pt2-dot" aria-hidden="true"></span><span class="pt2-coln">${escapeHtml(c.label)}</span><span class="pt2-colc">${c.tasks.length}</span></div>
      ${c.tasks.length ? c.tasks.map((t) => cardHtml(t, vm, today)).join('') : `<span class="pt2-cempty">${c.key === 'waiting' ? 'Nothing is stuck.' : 'None.'}</span>`}
    </div>`).join('')}
  </div>`;
}

function briefHtml(parts) {
  return parts.map((p) => {
    if (typeof p === 'string') return escapeHtml(p);
    if (p.taskId) return `<button type="button" class="pt2-blink" data-open="${escapeHtml(p.taskId)}">${escapeHtml(p.text)}</button>`;
    if (p.hot) return `<span class="pt2-bhot">${escapeHtml(p.text)}</span>`;
    return escapeHtml(p.text || '');
  }).join('');
}

/** The add line's instruction options: live instructions, newest first, then none. */
export function addOptions(groups, picked) {
  const live = groups.filter((g) => !g.none && !g.closed);
  const sel = picked === undefined || picked === null ? (live[0] ? live[0].seq : '') : String(picked);
  return { sel, options: live.map((g) => ({ value: g.seq, label: `I${g.seq} · ${instrTitle(g.instruction)}` })).concat([{ value: '', label: 'Not on an instruction' }]) };
}

export function projectTasksHtml(vm = {}) {
  const today = vm.today || dayKey();
  const tasks = vm.tasks || [];
  const mode = vm.mode === 'board' ? 'board' : 'list';
  const minsBySeq = vm.minsBySeq || {};
  const allGroups = byInstruction(tasks, vm.instructions || [], minsBySeq, { showDone: true });
  const groups = byInstruction(tasks, vm.instructions || [], minsBySeq, { showDone: !!vm.showDone });
  const doneN = tasks.filter((t) => t.state === 'done').length;
  const brief = projectBrief({ tasks, people: vm.people || [], groups: allGroups, today });
  const add = addOptions(allGroups, vm.addInstr);
  const num = String(vm.projectNumber || '').trim();

  const modeBtn = (k, label) => `<button type="button" class="pt2-mode" data-mode="${k}" aria-pressed="${mode === k}">${label}</button>`;
  const head = `<div class="pt2-cols" aria-hidden="true"><span></span><span>Task</span><span>Assignee</span><span>State</span><span>Due</span></div>`;

  return `<div class="pt2">
    <div class="pt2-brief">
      <span class="pt2-brief-l"><span class="pt2-eyebrow">On this project</span>${vm.updatedAt ? `<span class="pt2-upd">Updated ${escapeHtml(vm.updatedAt)}</span>` : ''}</span>
      <p class="pt2-brief-p">${briefHtml(brief)}</p>
    </div>
    <div class="pt2-bar">
      <label class="pt2-add">
        <input class="pt2-in" id="pt2-add" type="text" autocomplete="off" maxlength="300"
               placeholder="${escapeHtml(`Add a task to ${num || 'this project'}, Enter to save`)}" aria-label="New task title">
        <span class="pt2-instr-w"><select class="pt2-instr" id="pt2-instr" aria-label="Instruction">${add.options.map((o) =>
          `<option value="${escapeHtml(o.value)}"${o.value === add.sel ? ' selected' : ''}>${escapeHtml(o.label)}</option>`).join('')}</select></span>
      </label>
      <span class="pt2-modes" role="group" aria-label="View">${modeBtn('list', 'By instruction')}${modeBtn('board', 'Board')}</span>
      ${mode === 'list' && doneN ? `<button type="button" class="pt2-done" data-showdone>${vm.showDone ? 'Hide done' : `Show done (${doneN})`}</button>` : ''}
    </div>
    ${vm.note ? `<p class="pt2-note" role="alert">${escapeHtml(vm.note)}</p>` : ''}
    ${mode === 'board' ? boardHtml(tasks, vm, today) : `<div class="pt2-table">${head}${groups.map((g) => groupHtml(g, vm, today)).join('')}${
      groups.length ? '' : '<p class="pt2-empty">No tasks on this project yet.</p>'}</div>`}
  </div>`;
}

/**
 * Mount (or repaint) the tab. The add line's text, focus and caret survive a
 * repaint, so a tick landing while someone types does not eat their sentence.
 *
 * handlers: onTick(id) · onOpen(id) · onAdd({ title, instructionId }) → Promise
 *           onMode('list'|'board') · onShowDone() · onPickInstr(seq)
 */
export function mountProjectTasks(host, vm, handlers = {}) {
  const prev = host.querySelector('#pt2-add');
  const keep = prev ? { v: prev.value, focus: document.activeElement === prev, s: prev.selectionStart, e: prev.selectionEnd } : null;
  host.innerHTML = projectTasksHtml(vm);
  if (keep) {
    const inp = host.querySelector('#pt2-add');
    inp.value = keep.v;
    if (keep.focus) { inp.focus(); try { inp.setSelectionRange(keep.s, keep.e); } catch (e) { /* not a text input */ } }
  }
  host._pt2 = handlers;
  if (host.dataset.pt2Bound) return;
  host.dataset.pt2Bound = '1';
  const h = () => host._pt2 || {};
  host.addEventListener('click', (e) => {
    const tick = e.target.closest('[data-tick]');
    if (tick) { e.stopPropagation(); return h().onTick?.(tick.dataset.tick); }
    const m = e.target.closest('[data-mode]');
    if (m) return h().onMode?.(m.dataset.mode);
    if (e.target.closest('[data-showdone]')) return h().onShowDone?.();
    const open = e.target.closest('[data-open]');
    if (open) return h().onOpen?.(open.dataset.open);
  });
  host.addEventListener('keydown', (e) => {
    if (e.target.id === 'pt2-add' && e.key === 'Enter' && !e.isComposing) {
      e.preventDefault();
      const inp = e.target;
      const title = inp.value.replace(/\s+/g, ' ').trim();
      if (!title || inp.dataset.busy) return;
      const sel = host.querySelector('#pt2-instr');
      inp.dataset.busy = '1';
      Promise.resolve(h().onAdd?.({ title, instructionId: sel && sel.value ? sel.value : null }))
        .then((ok) => { const cur = host.querySelector('#pt2-add'); if (cur && ok !== false) cur.value = ''; })
        .finally(() => { const cur = host.querySelector('#pt2-add'); if (cur) delete cur.dataset.busy; });
      return;
    }
    if ((e.key === 'Enter' || e.key === ' ') && e.target.matches('.pt2-row[data-open], .pt2-card[data-open]')) {
      e.preventDefault();
      h().onOpen?.(e.target.dataset.open);
    }
  });
  host.addEventListener('change', (e) => {
    if (e.target.id === 'pt2-instr') h().onPickInstr?.(e.target.value);
  });
}

export { isOpen };
